import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatStepperModule } from '@angular/material/stepper';
import { RouterModule } from '@angular/router';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { LightboxModule } from 'ngx-lightbox';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgPipesModule } from 'ngx-pipes';

import {
    AutofocusDirective,
    NumberOnlyDirective,
    DebounceKeyupDirective,
    NoScrollNumberInputDirective,
    FeatureFlagDirective,
    DecimalLimitDirective,
} from '@app/core/_base/layout';
import { AtMentionModule } from '@app/core/_common/at-mention/at-mention.module';
import { CoreModule } from '@app/core/core.module';
import { PartialsModule } from '@app/layout-sidebar/partials/partials.module';
import { ClientPurchaseOrderPopupFormComponent } from '@app/modules/clients/client-purchase-order-popup-form/client-purchase-order-popup-form.component';
import { PopupFormComponent as ClientUserPopupForm } from '@app/modules/clients/popup-form/popup-form.component';
import { AuthFormModalComponent as FactoryModalComponent } from '@app/modules/factories/form-modal/form-modal.component';
import { CreateClientRfqComponent } from '@app/modules/inventory/create-client-rfq/create-client-rfq.component';
import { RfqKitItemsComponent } from '@app/modules/inventory/rfq-kit-items/rfq-kit-items.component';
import { RfqStockLocationsComponent } from '@app/modules/inventory/rfq-stock-locations/rfq-stock-locations.component';
import { RfqVariantSkusPopupComponent } from '@app/modules/inventory/rfq-variant-skus-popup/rfq-variant-skus-popup.component';
import { PrintLabelComponent } from '@app/modules/purchase-orders/print-label/print-label.component';
import { ReceivingReportFormComponent } from '@app/modules/purchase-orders/receiving-report-form/receiving-report-form.component';
import { ReceivingReportFormRfqsComponent } from '@app/modules/purchase-orders/receiving-report-form-rfqs/receiving-report-form-rfqs.component';
import { ReceivingReportsComponent } from '@app/modules/purchase-orders/receiving-reports/receiving-reports.component';
import { RfqLotFormComponent } from '@app/modules/purchase-orders/rfq-lot-form/rfq-lot-form.component';
import { PostedOrdersViewComponent } from '@app/modules/shipping/posted-orders-view/posted-orders-view.component';
import { PostedOrdersViewModalComponent } from '@app/modules/shipping/posted-orders-view-modal/posted-orders-view-modal.component';
import { CaretComponent } from '@app/shared/components/caret/caret.component';
import { ShippingAddressInlineTableComponent } from '@app/shared/components/clients/shipping-address-inline-table/shipping-address-inline-table.component';
import { ShippingAddressPopupFormComponent } from '@app/shared/components/clients/shipping-address-popup-form/shipping-address-popup-form.component';
import { DynamicColumnsComponent } from '@app/shared/components/dynamic-columns/dynamic-columns.component';
import { DynamicFiltersComponent } from '@app/shared/components/dynamic-filters/dynamic-filters.component';
import { FilterCategoryComponent } from '@app/shared/components/dynamic-filters/filters/filter-category/filter-category.component';
import { FilterChannelsComponent } from '@app/shared/components/dynamic-filters/filters/filter-channels/filter-channels.component';
import { FilterClientPaymentTermComponent } from '@app/shared/components/dynamic-filters/filters/filter-client-payment-term/filter-client-payment-term.component';
import { FilterClientPurchaseOrderTypeComponent } from '@app/shared/components/dynamic-filters/filters/filter-client-purchase-order-type/filter-client-purchase-order-type.component';
import { FilterClientTypesComponent } from '@app/shared/components/dynamic-filters/filters/filter-client-types/filter-client-types.component';
import { FilterClientsComponent } from '@app/shared/components/dynamic-filters/filters/filter-clients/filter-clients.component';
import { FilterProductDeptComponent } from '@app/shared/components/dynamic-filters/filters/filter-product-dept/filter-product-dept.component';
import { FilterSourcingAssigneeComponent } from '@app/shared/components/dynamic-filters/filters/filter-sourcing-assignee/filter-sourcing-assignee.component';
import { FilterStaffComponent } from '@app/shared/components/dynamic-filters/filters/filter-staff/filter-staff.component';
import { FilterSubCategoryComponent } from '@app/shared/components/dynamic-filters/filters/filter-sub-category/filter-sub-category.component';
import { FilterTagsComponent } from '@app/shared/components/dynamic-filters/filters/filter-tags/filter-tags.component';
import { FilterToggleComponent } from '@app/shared/components/dynamic-filters/filters/filter-toggle/filter-toggle.component';
import { FilterWarehouseLocationComponent } from '@app/shared/components/dynamic-filters/filters/filter-warehouse-location/filter-warehouse-location.component';
import { FilterCustomFieldComponent } from '@app/shared/components/dynamic-filters/filters/filter-custom-field/filter-custom-field.component';
import { IconComponent } from '@app/shared/components/icon/icon.component';
import { ScanLocationComponent } from '@app/shared/components/scan-location/scan-location.component';
import { ScanModeModalComponent } from '@app/shared/components/scan-mode-modal/scan-mode-modal.component';
import { TabLabelComponent } from '@app/shared/components/tab-label/tab-label.component';
import { LuminousV2Module } from 'v2/luminous-v2.module';
import { FeatureModule } from 'v2/shared/feature/feature.module';

import { AttachmentsViewComponent } from './components/attachments-view/attachments-view.component';
import { ClientFormComponent } from './components/client-form/client-form.component';
import { ClientSelectComponent } from './components/client-select/client-select.component';
import { FilterCompanyComponent } from './components/dynamic-filters/filters/filter-company/filter-company.component';
import { FilterFactoryComponent } from './components/dynamic-filters/filters/filter-factory/filter-factory.component';
import { FilterFulfillmentStatusComponent } from './components/dynamic-filters/filters/filter-fulfillment-status/filter-fulfillment-status.component';
import { FilterMultiInputComponent } from './components/dynamic-filters/filters/filter-multi-input/filter-multi-input.component';
import { FilterMultiselectComponent } from './components/dynamic-filters/filters/filter-multiselect/filter-multiselect.component';
import { FilterPaymentStatusComponent } from './components/dynamic-filters/filters/filter-payment-status/filter-payment-status.component';
import { FilterSalesRepAssigneeComponent } from './components/dynamic-filters/filters/filter-sales-rep-assignee/filter-sales-rep-assignee.component';
import { FilterWarehouseGroupComponent } from './components/dynamic-filters/filters/filter-warehouse-group/filter-warehouse-group.component';
import { ExportTemplateFormComponent } from './components/export-template/components/export-template-form/export-template-form.component';
import { ExportTemplateComponent } from './components/export-template/export-template.component';
import { FulfillmentStatusComponent } from './components/fulfillment-status/fulfillment-status.component';
import { IconSelectorComponent } from './components/icon-selector/icon-selector.component';
import { ImportPreviewComponent } from './components/import-preview/import-preview.component';
import { ColumnMapperComponent } from './components/importer/column-mapper/column-mapper.component';
import { ImportResultComponent } from './components/importer/import-result/import-result.component';
import { ImportStatusComponent } from './components/importer/import-status/import-status.component';
import { ImporterComponent } from './components/importer/importer/importer.component';
import { JobStatusDetailsPopupComponent } from './components/job-status/job-status-details-popup/job-status-details-popup.component';
import { JobStatusLinkComponent } from './components/job-status/job-status-link/job-status-link.component';
import { JobOutputDialogComponent } from './components/job-status/job-output-dialog/job-output-dialog.component';
import { PicklistLocationComponent } from './components/picklist-location/picklist-location.component';
import { AddOrderItemsFormComponent } from './components/purchase-orders/add-order-items-form/add-order-items-form.component';
import { AuthFormModalComponent } from './components/purchase-orders/form-modal/form-modal.component';
import { PurchaseOrderFormComponent } from './components/purchase-orders/purchase-order-form/purchase-order-form.component';
import { RfqOrderFormComponent } from './components/rfq-order-form/rfq-order-form.component';
import { RfqOrderPaymentComponent } from './components/rfq-order-payment/rfq-order-payment.component';
import { SalesOrderStarredPopupFormComponent } from './components/sales-order-starred-popup-form/sales-order-starred-popup-form.component';
import { SalesOrderTrackingNumberPopupFormComponent } from './components/sales-order-tracking-number-popup-form/sales-order-tracking-number-popup-form.component';
import { SkuLogDetailRemarksComponent } from './components/sku-log-detail-remarks/sku-log-detail-remarks.component';
import { SubscriptionPaymentFormComponent } from './components/subscription-payment-form/subscription-payment-form.component';
import { InlineMarginCalculatorComponent } from './components/tools/inline-margin-calculator/inline-margin-calculator.component';
import { InlineMarginCalculatorKitComponent } from './components/tools/inline-margin-calculator-kit/inline-margin-calculator-kit.component';
import { UiCountriesComponent } from './components/ui-countries/ui-countries.component';
import { UiStatesComponent } from './components/ui-states/ui-states.component';
import { ViewFactoryComponent } from './components/view-factory/view-factory.component';
import { BillingPeriodStringPipe } from './pipes/billing-period-string.pipe';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { CompanyNamesPipe } from './pipes/company-names/company-names.pipe';
import { ConvertIsoStringPipe } from './pipes/convert-iso-string.pipe';
import { ExcerptPipe } from './pipes/excerpt.pipe';
import { FilterByIdPipe } from './pipes/filter-by-id.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { HasShippingLabelPipe } from './pipes/has-shipping-label.pipe';
import { ImagesOnlyPipe } from './pipes/images-only.pipe';
import { KeyMatchPipe } from './pipes/key-match/key-match.pipe';
import { LinkifyPipe } from './pipes/linkify.pipe';
import { NotImageOnlyPipe } from './pipes/not-image-only.pipe';
import { PermissionPipe } from './pipes/permission.pipe';
import { PickPackStatusPipe } from './pipes/pick-pack-status.pipe';
import { PurchaseOrderDisplayFormatPipe } from './pipes/purchase-order-display-format.pipe';
import { PurchaseOrderFormatPipe } from './pipes/purchase-order-format.pipe';
import { QtyTypePipe } from './pipes/qty-type.pipe';
import { StatePipe } from './pipes/state/state.pipe';
import { StringReplacePipe } from './pipes/string-replace.pipe';
import { StripHtmlPipe } from './pipes/strip-html.pipe';
import { UomDisplayPipe } from './pipes/uom-display.pipe';
import { SharedComponentsV1Module } from './shared-components-v1.module';
import { DirectiveModule } from 'v2/modules/shared/directives/directive.module';

@NgModule({
  declarations: [
    AttachmentsViewComponent,
    ImagesOnlyPipe,
    LinkifyPipe,
    NotImageOnlyPipe,
    BillingPeriodStringPipe,
    ClientSelectComponent,
    ClientFormComponent,
    RfqOrderFormComponent,
    RfqOrderPaymentComponent,
    SubscriptionPaymentFormComponent,
    ViewFactoryComponent,
    PurchaseOrderFormComponent,
    AddOrderItemsFormComponent,
    PurchaseOrderFormatPipe,
    CapitalizePipe,
    FilterPipe,
    InlineMarginCalculatorComponent,
    InlineMarginCalculatorKitComponent,
    FilterByIdPipe,
    ExcerptPipe,
    StripHtmlPipe,
    IconSelectorComponent,
    ImportPreviewComponent,
    AuthFormModalComponent,
    CreateClientRfqComponent,
    ClientPurchaseOrderPopupFormComponent,
    ClientUserPopupForm,
    FactoryModalComponent,
    RfqVariantSkusPopupComponent,
    AutofocusDirective,
    NumberOnlyDirective,
    DebounceKeyupDirective,
    ConvertIsoStringPipe,
    StringReplacePipe,
    QtyTypePipe,
    NoScrollNumberInputDirective,
    DynamicFiltersComponent,
    FilterWarehouseLocationComponent,
    FilterCustomFieldComponent,
    FilterProductDeptComponent,
    FilterChannelsComponent,
    FilterClientsComponent,
    FilterTagsComponent,
    FilterSourcingAssigneeComponent,
    FilterCategoryComponent,
    FilterSubCategoryComponent,
    FilterToggleComponent,
    FilterPaymentStatusComponent,
    CaretComponent,
    DynamicColumnsComponent,
    FilterClientPurchaseOrderTypeComponent,
    FilterStaffComponent,
    FilterClientPaymentTermComponent,
    TabLabelComponent,
    CompanyNamesPipe,
    ShippingAddressInlineTableComponent,
    ShippingAddressPopupFormComponent,
    FilterClientTypesComponent,
    ScanLocationComponent,
    ScanModeModalComponent,
    ReceivingReportsComponent,
    ReceivingReportFormComponent,
    ReceivingReportFormRfqsComponent,
    FilterFactoryComponent,
    UiCountriesComponent,
    UiStatesComponent,
    StatePipe,
    KeyMatchPipe,
    RfqKitItemsComponent,
    ExportTemplateComponent,
    ExportTemplateFormComponent,
    RfqLotFormComponent,
    PrintLabelComponent,
    FilterSalesRepAssigneeComponent,
    FilterCompanyComponent,
    RfqStockLocationsComponent,
    FeatureFlagDirective,
    JobStatusLinkComponent,
    JobStatusDetailsPopupComponent,
    JobOutputDialogComponent,
    ImporterComponent,
    ColumnMapperComponent,
    ImportStatusComponent,
    ImportResultComponent,
    PicklistLocationComponent,
    FulfillmentStatusComponent,
    FilterWarehouseGroupComponent,
    SalesOrderTrackingNumberPopupFormComponent,
    SalesOrderStarredPopupFormComponent,
    SkuLogDetailRemarksComponent,
    FilterMultiselectComponent,
    FilterMultiInputComponent,
    FilterFulfillmentStatusComponent,
    DecimalLimitDirective,
    PurchaseOrderDisplayFormatPipe,
    HasShippingLabelPipe,
    PickPackStatusPipe,
    PermissionPipe,
    PostedOrdersViewModalComponent,
    PostedOrdersViewComponent,
    JobOutputDialogComponent,
  ],
  imports: [
    CommonModule,
    TranslateModule,
    MatStepperModule,
    PartialsModule,
    NgbModule,
    LightboxModule,
    MatCheckboxModule,
    MatIconModule,
    MatTabsModule,
    NgSelectModule,
    MatRadioModule,
    ReactiveFormsModule,
    NgPipesModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    AngularEditorModule,
    AtMentionModule,
    DragDropModule,
    MatMenuModule,
    MatDialogModule,
    MatProgressBarModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatChipsModule,
    CoreModule,
    RouterModule,
    LuminousV2Module,
    SharedComponentsV1Module,
    FeatureModule,
    IconComponent,
    UomDisplayPipe,
    DirectiveModule
  ],
  exports: [
    AttachmentsViewComponent,
    ImagesOnlyPipe,
    LinkifyPipe,
    BillingPeriodStringPipe,
    NotImageOnlyPipe,
    ClientFormComponent,
    PurchaseOrderFormatPipe,
    CapitalizePipe,
    FilterPipe,
    HasShippingLabelPipe,
    FilterByIdPipe,
    PickPackStatusPipe,
    ExcerptPipe,
    StripHtmlPipe,
    InlineMarginCalculatorComponent,
    InlineMarginCalculatorKitComponent,
    ImportPreviewComponent,
    AuthFormModalComponent,
    AutofocusDirective,
    NumberOnlyDirective,
    DebounceKeyupDirective,
    ConvertIsoStringPipe,
    StringReplacePipe,
    QtyTypePipe,
    NoScrollNumberInputDirective,
    DynamicFiltersComponent,
    FilterWarehouseLocationComponent,
    FilterCustomFieldComponent,
    CaretComponent,
    DynamicColumnsComponent,
    TabLabelComponent,
    CompanyNamesPipe,
    ShippingAddressInlineTableComponent,
    ShippingAddressPopupFormComponent,
    IconComponent,
    ScanLocationComponent,
    ScanModeModalComponent,
    ReceivingReportsComponent,
    ReceivingReportFormComponent,
    ReceivingReportFormRfqsComponent,
    UiCountriesComponent,
    UiStatesComponent,
    StatePipe,
    KeyMatchPipe,
    NgxPermissionsModule,
    RfqLotFormComponent,
    PrintLabelComponent,
    RfqStockLocationsComponent,
    FeatureFlagDirective,
    DecimalLimitDirective,
    JobStatusLinkComponent,
    ImporterComponent,
    PicklistLocationComponent,
    FulfillmentStatusComponent,
    SalesOrderTrackingNumberPopupFormComponent,
    SalesOrderStarredPopupFormComponent,
    SkuLogDetailRemarksComponent,
    CoreModule,
    CreateClientRfqComponent,
    PermissionPipe,
    FeatureModule,
    PostedOrdersViewModalComponent,
    PostedOrdersViewComponent,
    DirectiveModule
  ],
})
export class SharedModule {}
